import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ThemeColors } from './theme.service';

export interface ThemeEvent {
  type: 'COLOR_UPDATE' | 'THEME_LOADED' | 'THEME_ERROR' | 'COLOR_SAVED';
  payload?: any;
  timestamp: Date;
}

export interface ColorUpdateEvent extends ThemeEvent {
  type: 'COLOR_UPDATE';
  payload: ThemeColors;
}

export interface ThemeLoadedEvent extends ThemeEvent {
  type: 'THEME_LOADED';
  payload: ThemeColors;
}

export interface ThemeErrorEvent extends ThemeEvent {
  type: 'THEME_ERROR';
  payload: { error: any; fallbackColors: ThemeColors };
}

export interface ColorSavedEvent extends ThemeEvent {
  type: 'COLOR_SAVED';
  payload: ThemeColors;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeEventBus {
  private eventSubject = new Subject<ThemeEvent>();
  public events$ = this.eventSubject.asObservable();

  /**
   * Emit color update event
   */
  emitColorUpdate(colors: ThemeColors): void {
    this.emitEvent({
      type: 'COLOR_UPDATE',
      payload: colors,
      timestamp: new Date()
    });
  }

  /**
   * Emit theme loaded event
   */
  emitThemeLoaded(colors: ThemeColors): void {
    this.emitEvent({
      type: 'THEME_LOADED',
      payload: colors,
      timestamp: new Date()
    });
  }

  /**
   * Emit theme error event
   */
  emitThemeError(error: any, fallbackColors: ThemeColors): void {
    this.emitEvent({
      type: 'THEME_ERROR',
      payload: { error, fallbackColors },
      timestamp: new Date()
    });
  }

  /**
   * Emit color saved event
   */
  emitColorSaved(colors: ThemeColors): void {
    this.emitEvent({
      type: 'COLOR_SAVED',
      payload: colors,
      timestamp: new Date()
    });
  }

  /**
   * Subscribe to specific event types
   */
  onColorUpdate(): Observable<ColorUpdateEvent> {
    return this.events$.pipe(
      filter((event): event is ColorUpdateEvent => event.type === 'COLOR_UPDATE')
    );
  }

  onThemeLoaded(): Observable<ThemeLoadedEvent> {
    return this.events$.pipe(
      filter((event): event is ThemeLoadedEvent => event.type === 'THEME_LOADED')
    );
  }

  onThemeError(): Observable<ThemeErrorEvent> {
    return this.events$.pipe(
      filter((event): event is ThemeErrorEvent => event.type === 'THEME_ERROR')
    );
  }

  onColorSaved(): Observable<ColorSavedEvent> {
    return this.events$.pipe(
      filter((event): event is ColorSavedEvent => event.type === 'COLOR_SAVED')
    );
  }

  /**
   * Private method to emit events
   */
  private emitEvent(event: ThemeEvent): void {
    console.log(`🎯 Theme Event: ${event.type}`, event.payload);
    this.eventSubject.next(event);
  }
}

